import { useMemo } from "react";
import {
  Box,
  Button,
  Icon,
  Select,
  SelectCombobox,
  SelectComboboxPopup,
  SelectComboboxTrigger,
  SelectEmpty,
  SelectGroup,
  SelectGroupLabel,
  SelectItem,
  SelectPopup,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Text,
  useSelectQuery,
} from "@salestrak/ui";

import type { FilterConfig, FilterValue } from "#app/lib/filters/types";
import type { IssueScope } from "#app/lib/filters/issue-scope";
import { scopeWorkflowId } from "#app/lib/filters/issue-scope";
import { FilterValueInput } from "./filter-value-input";
import { useFilterTypeAvailability } from "./use-filter-type-availability";
import type { Omittable } from "@salestrak/ui";

/** The `icon-sm` remove button's footprint, held open on the predicate line so
 *  the two lines share a right edge. */
const REMOVE_COL_WIDTH = 28;

type FieldOption = { value: string; label: string; isCustomField: boolean };

type FilterRowProps = Omittable<{
  config: FilterConfig;
  filter: FilterValue;
  onUpdate: (update: Partial<FilterValue>) => void;
  onRemove: () => void;
  /** The screen's scope. A screen pinned to one workflow has answered
   *  "which workflow?" for every row it can show, so that field drops from
   *  the list (the same rule the Simple panel applies), and the stage picker
   *  offers only that workflow's stages. */
  scope?: IssueScope;
}>;

/**
 * The field list, grouped: built-in fields, then the workspace's custom fields
 * under their own heading — a workspace-authored vocabulary that would make
 * the built-in list harder to scan mixed in. While the field is being
 * searched the grouping collapses to the flat matches: a heading over a group
 * whose rows all filtered out, and the divider before it, would dangle.
 */
function FieldOptions({ options }: { options: FieldOption[] }) {
  const { hasQuery, matches } = useSelectQuery();
  const visible = options.filter((opt) => matches(opt.label));
  const builtIn = visible.filter((opt) => !opt.isCustomField);
  const custom = visible.filter((opt) => opt.isCustomField);
  const item = (opt: FieldOption) => (
    <SelectItem key={opt.value} value={opt.value}>
      <Text>{opt.label}</Text>
    </SelectItem>
  );
  if (hasQuery) return <>{visible.map(item)}</>;
  return (
    <>
      {builtIn.map(item)}
      {custom.length > 0 ? (
        <>
          {builtIn.length > 0 ? <SelectSeparator /> : null}
          <SelectGroup>
            <SelectGroupLabel>Custom fields</SelectGroupLabel>
            {custom.map(item)}
          </SelectGroup>
        </>
      ) : null}
    </>
  );
}

/**
 * One condition of an advanced group, built up as it is filled in.
 *
 * Line one is the field beside its remove button. The field control is a
 * searchable select — the list runs long once a workspace has custom fields,
 * and typing in the field itself narrows it. Picking a field auto-selects its
 * first operator and clears the value, and only then does line two appear:
 * the operator and the value in equal halves ending on the field's edge. A
 * no-value operator (isNull, isEmpty, …) gives the operator the whole line.
 * Nothing renders for a control that has nothing to say yet — a disabled
 * operator over a field that does not exist is chrome without information.
 */
export function FilterRow({
  config,
  filter,
  onUpdate,
  onRemove,
  scope,
}: FilterRowProps) {
  const isAvailable = useFilterTypeAvailability();
  const scopedWorkflowId = scope ? scopeWorkflowId(scope) : undefined;
  const fieldOptions = useMemo<FieldOption[]>(
    () =>
      Object.values(config.fields)
        // A field over an empty pool is a dead end (its value input has
        // nothing to pick) and drops from the menu — unless this row already
        // holds it, so an applied filter stays inspectable. A workflow field
        // under a single-workflow scope is dead the same way: its one useful
        // setting is already in force.
        .filter(
          (f) =>
            f.field === filter.field ||
            (isAvailable(f.type) && !(scopedWorkflowId && f.type === "workflow")),
        )
        .map((f) => ({
          value: f.field,
          label: f.label,
          isCustomField: f.type === "custom-field",
        })),
    [config.fields, isAvailable, filter.field, scopedWorkflowId]
  );

  const selectedField = filter.field ? config.fields[filter.field] : null;

  const operatorOptions = useMemo(
    () =>
      selectedField?.operators.map((op) => ({
        value: op.value,
        label: op.label,
      })) ?? [],
    [selectedField]
  );

  const selectedOperator = selectedField?.operators.find(
    (op) => op.value === filter.operator
  );

  const showValueInput = selectedOperator && selectedOperator.input !== "none";

  return (
    <Box gap="xs">
      <Box direction="row" align="center" gap="sm">
        <Box fill="flex">
          <SelectCombobox
            items={fieldOptions}
            value={filter.field || null}
            onValueChange={(next) => {
              if (!next) return;
              const fieldDef = config.fields[next];
              const firstOp = fieldDef?.operators[0];
              onUpdate({
                field: next,
                operator: firstOp?.value ?? "eq",
                value: null,
              });
            }}
          >
            <SelectComboboxTrigger aria-label="Filter field" size="sm" placeholder="Filter" />
            <SelectComboboxPopup aria-label="Fields">
              <FieldOptions options={fieldOptions} />
              <SelectEmpty>No matching fields</SelectEmpty>
            </SelectComboboxPopup>
          </SelectCombobox>
        </Box>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onRemove}
          aria-label="Remove filter"
        >
          <Icon name="trash" size="lg" color="muted" />
        </Button>
      </Box>

      {selectedField ? (
        // The halves are column boxes so their controls stretch to fill; the
        // operator trigger hugs (`width="auto"`) so the flex split, not the
        // full-width trigger's 144px floor, sets the halves.
        <Box direction="row" align="center" gap="sm">
          <Box flex={1} direction="column">
            <Select
              items={operatorOptions}
              value={filter.operator ?? null}
              onValueChange={(next: string | null) => {
                if (!next) return;
                onUpdate({
                  operator: next as FilterValue["operator"],
                  value: null,
                });
              }}
            >
              <SelectTrigger aria-label="Condition" size="sm" width="auto">
                <SelectValue placeholder="Is" />
              </SelectTrigger>
              <SelectPopup>
                {operatorOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <Text>{opt.label}</Text>
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          </Box>
          {showValueInput ? (
            <Box flex={1} direction="column">
              <FilterValueInput
                fieldType={selectedField.type}
                customField={selectedField.customField}
                value={filter.value}
                onChange={(value) => onUpdate({ value })}
                multiple={selectedOperator?.input === "multiple"}
                scope={scope}
              />
            </Box>
          ) : null}
          <Box width={REMOVE_COL_WIDTH} />
        </Box>
      ) : null}
    </Box>
  );
}
